import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  Badge,
  ActionIcon,
  Alert,
  Modal,
  Button,
} from '@mantine/core';
import { useMedplumNavigate } from '@medplum/react';
import { IconArrowLeft, IconUser, IconInfoCircle } from '@tabler/icons-react';
import { useState } from 'react';
import { Practitioner } from '@medplum/fhirtypes';
import { formatHumanName, SearchRequest } from '@medplum/core';
import { SearchControl } from '@/components/medplum-react/SearchControl/SearchControl';
import { ARKON } from '@consts/systems.consts';
import CreateInternalUserModal from './CreateInternalUserModal';
import { useTranslation } from 'react-i18next';
import { useDisclosure } from '@mantine/hooks';
import { useUserManagementStats, usePractitionerDeletion } from '../hooks';

// -----------------------------------------------------------------------------
export const UserManagementPage = (): JSX.Element => {
  const navigate = useMedplumNavigate();
  const { t } = useTranslation(['admin', 'common']);

  const { stats, refetch: refetchStats } = useUserManagementStats();
  const { deletePractitioner, deleting } = usePractitionerDeletion();

  const [deleteModalOpened, { close: closeDeleteModal }] = useDisclosure(false);
  const [practitionerToDelete, setPractitionerToDelete] =
    useState<Practitioner | null>(null);

  const searchRequest: SearchRequest = {
    resourceType: 'Practitioner',
    fields: ['name', 'telecom', 'active', '_lastUpdated'],
    sortRules: [{ code: 'name' }],
  };

  // ---------------------------------------------------------------------------
  const handleDeletePractitioner = async () => {
    if (!practitionerToDelete) {
      return;
    }

    const success = await deletePractitioner(practitionerToDelete);
    if (success) {
      refetchStats();
      closeDeleteModal();
      setPractitionerToDelete(null);
    }
  };

  // ---------------------------------------------------------------------------
  const renderPractitionerCell = (resource: any, field: { name: string }) => {
    if (resource.resourceType === 'Practitioner') {
      const practitioner = resource as Practitioner;

      if (field.name === 'name') {
        const isExternal = practitioner.extension?.some(
          (ext) => ext.url === ARKON.SD.EXTERNAL_ORGANIZATION_EXTENSION
        );

        const name = formatHumanName(practitioner.name?.[0]);

        return (
          <Group gap="xs" align="center">
            <IconUser size={16} />
            {name}
            {isExternal && (
              <Badge size="xs" color="orange" variant="light">
                External
              </Badge>
            )}
          </Group>
        );
      }

      if (field.name === 'telecom') {
        const email = practitioner.telecom?.find(
          (t) => t.system === 'email'
        )?.value;
        const phone = practitioner.telecom?.find(
          (t) => t.system === 'phone'
        )?.value;

        return (
          <Stack gap={2}>
            {email && <Text size="xs">{email}</Text>}
            {phone && (
              <Text size="xs" c="dimmed">
                {phone}
              </Text>
            )}
          </Stack>
        );
      }

      if (field.name === 'active') {
        return (
          <Badge
            color={practitioner.active !== false ? 'green' : 'red'}
            variant="filled"
            size="xs"
          >
            {practitioner.active !== false ? 'Active' : 'Inactive'}
          </Badge>
        );
      }
    }

    return undefined;
  };

  // ---------------------------------------------------------------------------
  const handleResourceClick = (event: any) => {
    navigate(`/Practitioner/${event.resource.id}`);
  };

  // ---------------------------------------------------------------------------
  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group align="center" gap="md">
          <ActionIcon
            variant="subtle"
            size="lg"
            onClick={() => navigate('/admin')}
          >
            <IconArrowLeft size={20} />
          </ActionIcon>
          <div>
            <Title order={1}>
              {t('userManagement.title', { ns: 'admin' })}
            </Title>
            <Text c="dimmed">
              {t('userManagement.description', { ns: 'admin' })}
            </Text>
          </div>
        </Group>
        {/* Info Alert */}
        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          {t('userManagement.infoAlert.message', { ns: 'admin' })}
        </Alert>{' '}
        {/* Statistics Cards */}
        <Group>
          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">
                {t('userManagement.stats.totalUsers', { ns: 'admin' })}
              </Text>
              <IconUser size={20} color="var(--mantine-color-blue-6)" />
            </Group>
            <Text size="xl" fw={700}>
              {stats.totalUsers.toLocaleString()}
            </Text>
          </Paper>
        </Group>
        {/* User Management Section */}
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Stack gap="md">
            {' '}
            <Group justify="space-between" align="center">
              <Title order={2} size="h3">
                {t('userManagement.userList.title', { ns: 'admin' })}
              </Title>
              <CreateInternalUserModal onUserCreated={refetchStats} />
            </Group>
            <Text c="dimmed" size="sm">
              {t('userManagement.userList.description', { ns: 'admin' })}
            </Text>
            {/* User List with SearchControl */}
            <SearchControl
              search={searchRequest}
              onClick={handleResourceClick}
              renderCell={renderPractitionerCell}
              hideToolbar={false}
            />
          </Stack>
        </Paper>
        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title={t('userManagement.deleteModal.title', { ns: 'admin' })}
          centered
        >
          <Stack gap="md">
            <Text>
              {t('userManagement.deleteModal.message', { ns: 'admin' })}
              <strong>
                {practitionerToDelete
                  ? formatHumanName(practitionerToDelete.name?.[0])
                  : ''}
              </strong>
              ?
            </Text>
            <Text size="sm" c="dimmed">
              {t('userManagement.deleteModal.warning', { ns: 'admin' })}
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button
                variant="outline"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button
                color="red"
                onClick={handleDeletePractitioner}
                loading={deleting}
              >
                {t('delete', { ns: 'common' })}
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
};
